// The gazetteer's export/import contract: which `TaskPogoPlace` rows travel
// with an exported logbook or trip, what they look like on disk, and what an
// importer should create in the environment it lands in.
//
// WHY THIS EXISTS. A route waypoint's `placeId` names a row in the environment
// that wrote it. Carry that file to another environment -- dev to prod -- and
// the id resolves to nothing, the NAME has no row either, and
// resolveRouteAgainstPlaces drops the dead binding on the first read. The
// waypoint still draws from its stored snapshot, but it has left the gazetteer:
// it will never follow a later correction, and moving the place in /me/places
// no longer moves it. Carrying the rows in the file is what closes that.
//
// TWO RULES THE WHOLE MODULE IS BUILT ON:
//
// - `nameKey` is DERIVED, never read from the file. It is what
//   `placesByNameKey` matches on, so a row created from a hand-edited (or
//   merely stale) key is a row the app can never look up again -- invisible
//   until someone wonders why the typeahead won't offer a place that plainly
//   exists. Derived here, and again in `ensurePlaces`.
// - `id` never travels. Ids are per-environment; an imported one would either
//   collide or dangle. The name is the portable identity, which is the same
//   bet `resolveRouteAgainstPlaces`'s name bootstrap already makes.
//
// Pure, like PlaceFields.mjs and for the same reason: `scripts/backfill-places.mjs`
// imports `isPlaceCandidate` from here, and it cannot load `src/utilities/places.mjs`
// (which pulls in aws-amplify). Nothing here may import Database.mjs.

import { normalizePlaceName, FACILITY_FIELDS, validatePlace } from './PlaceFields.mjs';
import { isPlaced, looksLikeCoordinateName } from './TripRoute.mjs';

// Everything a place row takes with it. Deliberately absent:
//   id                      per-environment (see the header)
//   nameKey                 derived from `name`
//   owner/createdAt/
//   updatedAt/__typename    server-assigned; an import creates a NEW row
export const EXPORTED_PLACE_KEYS = [
  'name',
  'friendlyName',
  'lat',
  'lon',
  'source',
  'curated',
  'notes',
  ...FACILITY_FIELDS.map((field) => field.key),
];

const BOOLEAN_PLACE_KEYS = new Set(['curated', ...FACILITY_FIELDS.map((field) => field.key)]);

// One row in its on-disk shape, or null when it isn't one.
//
// A null/undefined value is OMITTED rather than written as false: for the
// facilities a null means "nobody has said", which is not the same claim as
// "no" (see FACILITY_FIELDS). Writing false would turn every blank in the
// source environment into a denial in the target.
export function exportedPlace(row) {
  if (!row || typeof row !== 'object') return null;
  const clean = {};
  for (const key of EXPORTED_PLACE_KEYS) {
    const value = row[key];
    if (value === null || value === undefined) continue;
    clean[key] = value;
  }
  return clean;
}

// The `places` section: one entry per distinct name, ordered so two exports of
// unchanged data produce the same bytes (a file that diffs is a file you can
// review). Rows the app itself would reject on read are dropped rather than
// written -- an invalid coordinate disables the overlay for every waypoint
// bound to it, so shipping one helps nobody.
export function serializePlaces(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    const key = normalizePlaceName(row?.name);
    if (!key || byKey.has(key)) continue;
    if (!validatePlace(row).ok) continue;
    const clean = exportedPlace(row);
    if (clean) byKey.set(key, clean);
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, clean]) => clean);
}

// The file's `places` section -> rows ready to create, each carrying a derived
// `nameKey`.
//
// NEVER THROWS, and drops rather than rejects. A junk section must not make an
// otherwise good logbook un-importable: the entries are the user's data, the
// places are a bonus. Every value is whitelisted and coerced, because the
// alternative is a hand-edited key reaching the mutation -- AppSync rejects a
// whole mutation for one unknown attribute, which would turn every place write
// into a failure the user only sees as a count.
// A numeric coordinate or NaN. `null`, `undefined` and `''` all become NaN
// rather than `Number`'s 0, so a missing or nulled-out coordinate is dropped by
// validatePlace instead of pinning the place at 0.
function toCoordinate(value) {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(value);
}

export function parsePlacesSection(raw) {
  if (!Array.isArray(raw)) return [];

  const byKey = new Map();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;

    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    const nameKey = normalizePlaceName(name);
    if (!nameKey || byKey.has(nameKey)) continue;

    // null/undefined/'' -> NaN, not 0: `Number(null)` and `Number('')` are 0,
    // which sits in range and passes validatePlace, so a hand-edited `"lat":
    // null` would pin a place at the equator instead of being dropped. A
    // coordinate that isn't a real number is no coordinate.
    const row = { nameKey, name, lat: toCoordinate(entry.lat), lon: toCoordinate(entry.lon) };
    // Provenance, coerced to the two values the app writes. Anything else --
    // including a missing one -- reads as a hand placement, matching
    // backfill-places' `wp.src === 'geocode' ? 'geocode' : 'user'`.
    row.source = entry.source === 'geocode' ? 'geocode' : 'user';

    for (const key of BOOLEAN_PLACE_KEYS) {
      // Only a real boolean survives: anything else is "nobody has said",
      // which is a null column, which is an omitted key.
      if (typeof entry[key] === 'boolean') row[key] = entry[key];
    }
    // Same rule as notes: a non-blank string or nothing at all. A blank
    // friendlyName is not a label, it is the absence of one -- writing '' would
    // be a column that reads as set and displays as nothing.
    for (const key of ['friendlyName', 'notes']) {
      if (typeof entry[key] === 'string' && entry[key].trim() !== '') row[key] = entry[key];
    }

    // The same validator the editor holds saves on. A row it rejects is one
    // `hasValidCoordinates` would reject on every read.
    if (!validatePlace(row).ok) continue;
    byKey.set(nameKey, row);
  }
  return [...byKey.values()];
}

// Could this waypoint be a place? The four rules, in one definition, shared
// with scripts/backfill-places.mjs so the two can never drift:
//
// - unplaced: no position to record.
// - autoName: the "name" is the app's own rendering of the coordinates.
// - coordinate-shaped: the same thing on a row written before `autoName` was
//   recorded reliably. Dev really does hold a via named "49°8.8'N 123°52.4'W"
//   with no flag on it.
// - blank: normalizes to a key nothing could ever look up.
export function isPlaceCandidate(waypoint) {
  if (!isPlaced(waypoint) || waypoint.autoName) return false;
  if (looksLikeCoordinateName(waypoint.name)) return false;
  return normalizePlaceName(waypoint.name) !== '';
}

// Every waypoint across `routes` that could be a place, as {nameKey, name,
// lat, lon, source}. Not deduped -- callers rank and collapse.
function placeCandidates(routes) {
  const candidates = [];
  for (const route of routes || []) {
    for (const waypoint of route?.waypoints || []) {
      if (!isPlaceCandidate(waypoint)) continue;
      candidates.push({
        nameKey: normalizePlaceName(waypoint.name),
        name: waypoint.name.trim(),
        lat: waypoint.lat,
        lon: waypoint.lon,
        // The waypoint's provenance IS the row's: `src` and `source` record
        // the same fact in the two schemas.
        source: waypoint.src === 'geocode' ? 'geocode' : 'user',
      });
    }
  }
  return candidates;
}

// The nameKeys a set of routes refers to.
export function placeNameKeysInRoutes(routes) {
  return new Set(placeCandidates(routes).map((candidate) => candidate.nameKey));
}

// Minimal pins built from the waypoints themselves — no notes, no facilities,
// nothing but a name and where it is. Used for a name the source environment
// had no row for (a route named before the gazetteer existed, or one
// backfill-places never ran over), and for a whole file exported by a client
// predating the `places` section.
//
// One row per name. A hand placement outranks a geocoder answer, for the same
// reason planPlaceBackfill ranks that way: correcting a pin is precisely the
// gesture the shared row exists to preserve. First-in-file breaks the rest, so
// a given file always produces the same rows.
export function placesFromRoutes(routes) {
  const byKey = new Map();
  for (const candidate of placeCandidates(routes)) {
    const existing = byKey.get(candidate.nameKey);
    if (!existing) {
      byKey.set(candidate.nameKey, candidate);
    } else if (existing.source !== 'user' && candidate.source === 'user') {
      byKey.set(candidate.nameKey, candidate);
    }
  }
  return [...byKey.values()];
}

// The rows an export file should carry: the gazetteer rows its routes actually
// refer to, and nothing else. `placeIndex` is loadPlaceIndex()'s {byId, byKey}.
//
// Resolution mirrors resolveRouteAgainstPlaces: the binding first, the name
// behind it. A waypoint whose row doesn't exist in THIS environment contributes
// nothing here -- the importer covers it with a minimal pin, which is the
// honest shape for a place the source gazetteer never held either.
//
// An absent or empty index yields no section at all, so an export never depends
// on the gazetteer being reachable.
export function placesForExport(routes, placeIndex) {
  const byId = placeIndex?.byId;
  const byKey = placeIndex?.byKey;
  if (!byId && !byKey) return [];

  const rows = [];
  for (const route of routes || []) {
    for (const waypoint of route?.waypoints || []) {
      if (!isPlaceCandidate(waypoint)) continue;
      const row =
        (waypoint.placeId && byId?.get(waypoint.placeId)) ||
        byKey?.get(normalizePlaceName(waypoint.name)) ||
        null;
      if (row) rows.push(row);
    }
  }
  return serializePlaces(rows);
}

// What an importer should ensure exists: the file's own section, plus a minimal
// pin for every name its routes refer to that the section doesn't cover.
//
// The union rather than one or the other, so nothing a route points at is left
// unbound -- a partial section (the source environment had no row for one of
// the names) would otherwise strand exactly those waypoints, which is the
// failure this whole feature exists to remove.
export function placesForImport(rawSection, routes) {
  const fromFile = parsePlacesSection(rawSection);
  const covered = new Set(fromFile.map((row) => row.nameKey));
  const gaps = placesFromRoutes(routes).filter((row) => !covered.has(row.nameKey));
  return [...fromFile, ...gaps];
}
